import '../styles/pdf-pages.css';
import '../styles/model-overview.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateVenueCo2 } from '@/domain/impact-calculate-venue/entities/calculate-venue-co2.entity';
import { selectImpactCalculateVenueCo2ForProject } from '@/domain/impact-calculate-venue/impact-calculate-venue.selectors';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatProjectDatesTz } from '@/ui/utils/format-project-dates';
import {
  getColorForScore,
  getColorScaleForScore,
} from '@/ui/utils/get-color-for-score';

import CalendarMonthSvg from '../assets/calendar_month.svg';
import { Co2InfographyCompact } from '../components/Co2InfographyCompact';
import { Co2Timeline } from '../components/Co2Timeline';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import styles from '../styles/VenueDateManagement.module.css';
import { displayDecimal } from '../utils/display-scores.utils';

export function PdfVenuePage2({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const venue = useAppSelector(
    selectCurrentProjectVenue,
  ) satisfies ProjectVenue | null;
  const venueId = venue?.id ?? 0;

  const impactCo2 = useAppSelector(
    selectImpactCalculateVenueCo2ForProject(venueId),
  ) satisfies CalculateVenueCo2;

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!venue) {
    return <h1>{translate('PdfExports.venue-not-found')}</h1>;
  }

  const totalStructureEmissions = impactCo2?.structure?.score ?? 0;
  const totalDatesEmissions = impactCo2?.dates?.score ?? 0;

  return (
    <div id="page2" className="pdf-page">
      <div className="content">
        <div className="pdf-light-header">
          <div>
            <h1>{venue.name}</h1>
            {venue.startDate && venue.endDate && (
              <h2>
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="venue-date">
                  {formatProjectDatesTz(
                    venue.startDate || '',
                    venue.endDate || '',
                  )}
                </span>
              </h2>
            )}
          </div>
        </div>
        <div className="pdf-separator"></div>

        <div className="model-header">
          <div>
            <h2>{translate('PdfExports.co2-score-details')}</h2>
          </div>
          <div>
            <h4>
              <span className="highlight">
                {displayDecimal(impactCo2.global.score)}{' '}
              </span>
              {translate('Projects.teCO2')}
            </h4>
            <h5>
              {displayDecimal(impactCo2.global.kgeCO2ByParticipant)}{' '}
              {translate('Projects.kgeCO2-by-participant')}
            </h5>
          </div>
          <div>
            <PdfScoreCard
              type="co2"
              scoreLetter={impactCo2.global.scoreLetter}
              size="medium"
            />
          </div>
        </div>

        <div className="model-content">
          <div
            style={{
              width: 350,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="sector" style={{ flex: '0 0 auto' }}>
              <div className="sector-header" style={{ paddingBottom: '10px' }}>
                <h4>{translate('PdfExports.structure-emissions-title')}</h4>
              </div>
              <div className="sector-value" style={{ padding: '10px 20px' }}>
                <div className={styles.emissionsRow}>
                  <div className={styles.emissionsBox}>
                    <span className={styles.emissionsLabel}>CO2</span>
                    <div className={styles.emissionsValue}>
                      {displayDecimal(totalStructureEmissions)}
                    </div>
                  </div>
                  <span>{translate('PdfExports.structure-emissions')}</span>
                </div>
              </div>
            </div>

            <div className="sector">
              <div className="sector-header">
                <h4>{translate('PdfExports.structure-emissions-simple')}</h4>
                <div>
                  <span className="highlight">
                    {displayDecimal(impactCo2.structure.score)}
                  </span>{' '}
                  {translate('Projects.teCO2')}
                </div>
              </div>
              <div className="sector-value">
                <ul>
                  {Object.entries(impactCo2.structure.sectors.site.categories)
                    .filter(([_, { score }]) => score !== 0)
                    .map(([key, { score }]) => (
                      <li key={key}>
                        <span>
                          {translate(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ('InviteExternalToFill.category.' + key) as any,
                          )}
                        </span>
                        <span>
                          {displayDecimal(score)} {translate('Projects.teCO2')}
                        </span>
                      </li>
                    ))}
                  {Object.entries(impactCo2.structure.sectors.team.categories)
                    .filter(([_, { score }]) => score !== 0)
                    .map(([key, { score }]) => (
                      <li key={key}>
                        <span>
                          {translate(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ('InviteExternalToFill.category.' + key) as any,
                          )}
                        </span>
                        <span>
                          {displayDecimal(score)} {translate('Projects.teCO2')}
                        </span>
                      </li>
                    ))}
                  {Object.entries(impactCo2.structure.sectors.public.categories)
                    .filter(([_, { score }]) => score !== 0)
                    .map(([key, { score }]) => (
                      <li key={key}>
                        <span>
                          {translate(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ('InviteExternalToFill.category.' + key) as any,
                          )}
                        </span>
                        <span>
                          {displayDecimal(score)} {translate('Projects.teCO2')}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            </div>

            {
              <Co2InfographyCompact
                items={[
                  {
                    score: totalStructureEmissions,
                    label: 'gestion structure',
                  },
                  { score: totalDatesEmissions, label: 'exploitation' },
                ]}
                colorScale={getColorScaleForScore(impactCo2.global.scoreLetter)}
                title={translate('PdfExports.co2-sector-emissions')}
              />
            }
          </div>

          <div
            style={{
              width: 350,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <div className="sector" style={{ flex: '0 0 auto' }}>
              <div className="sector-header" style={{ paddingBottom: '10px' }}>
                <h4>{translate('PdfExports.date-emissions-title')}</h4>
              </div>
              <div
                className="sector-value"
                style={{ padding: '10px 20px 13px' }}
              >
                <div className={styles.emissionsRow}>
                  <div className={styles.emissionsBox}>
                    <span className={styles.emissionsLabel}>CO2</span>
                    <div className={styles.emissionsValue}>
                      {displayDecimal(totalDatesEmissions)}
                    </div>
                  </div>
                  <span>{translate('PdfExports.date-emissions')}</span>
                </div>
              </div>
            </div>

            <div className="sector" style={{ flex: '0 0 auto' }}>
              <div className="sector-header" style={{ paddingBottom: '10px' }}>
                <h4>{translate('PdfExports.date-emissions2')}</h4>
              </div>
              <div className="sector-value" style={{ padding: '10px 20px' }}>
                <ul>
                  <li key="artists">
                    <span>Artistes</span>
                    <span>Détails à venir</span>
                  </li>
                  <li key="public">
                    <span>Public</span>
                    <span>Détails à venir</span>
                  </li>
                  <li key="site-and-tech">
                    <span>Site et technique</span>
                    <span>Détails à venir</span>
                  </li>
                </ul>
              </div>
            </div>

            <Co2Timeline
              data={impactCo2.dates.byMonth}
              color={getColorForScore(impactCo2.global.scoreLetter).main}
            />
            <div className="model-overall-comments">
              {translate('PdfExports.comment')}
            </div>
          </div>
        </div>

        <div className="navigation-controls">
          <a href="#page1" className="tag-pill">
            {translate('PdfExports.go-back-to-overview')}
          </a>
          <a href="#page3" className="tag-pill">
            {translate('PdfExports.go-to-env')}
          </a>
        </div>
      </div>

      <PdfProjectPager current={2} total={totalPages}></PdfProjectPager>
    </div>
  );
}
